import Icon from "./Icon";
import { guestFields } from "./nsdFields";
import "./events.css";

/* ── form primitives (ported from prototype, brand CSS) ───────────────────── */
const Field = ({ label, required, error, hint, children }) => (
  <div>
    {label && (
      <div className={`ev-field-label${error ? " is-error" : ""}`}>
        {label}
        {required && <span className="req"> *</span>}
      </div>
    )}
    {children}
    {error && <div className="ev-field-error">{error}</div>}
    {hint && !error && <div className="ev-field-hint">{hint}</div>}
  </div>
);

const TextInput = ({
  value,
  onChange,
  onBlur,
  placeholder,
  type = "text",
  error,
}) => (
  <input
    className={`ev-input${error ? " is-error" : ""}`}
    value={value || ""}
    type={type}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    onBlur={onBlur}
  />
);

const Select = ({
  value,
  onChange,
  onBlur,
  options,
  placeholder,
  disabled,
  error,
}) => (
  <select
    className={`ev-select${error ? " is-error" : ""}`}
    value={value || ""}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value)}
    onBlur={onBlur}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map((o) => (
      <option key={o} value={o}>
        {o}
      </option>
    ))}
  </select>
);

const Segmented = ({ value, onChange, options }) => {
  const sel = value || options[0]?.v;
  return (
    <div className="ev-segmented">
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          className={`ev-seg-btn${sel === o.v ? " is-on" : ""}`}
          onClick={() => onChange(o.v)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
};

const Button = ({
  kind = "primary",
  children,
  onClick,
  icon,
  disabled,
  busy,
}) => (
  <button
    className={`ev-btn ev-btn--${kind}`}
    onClick={onClick}
    disabled={disabled}
    aria-busy={busy || undefined}
  >
    {busy ? (
      <span className="ev-spinner" aria-hidden="true" />
    ) : (
      icon && <Icon name={icon} size={18} />
    )}
    {children}
  </button>
);

/* Render the guest fields straight from the shared field model. */
const GuestFormFields = ({ event, v, set, err, onTouch, vehicleOptions }) => {
  const fields = guestFields(event, v, vehicleOptions);

  const control = (f) => {
    const blur = () => onTouch(f.key);
    if (f.segmented) {
      const onCh = (x) => {
        const patch = { [f.key]: x };
        (f.clears || []).forEach((k) => (patch[k] = ""));
        set(patch);
      };
      return <Segmented value={v[f.key]} onChange={onCh} options={f.options} />;
    }
    if (f.select) {
      const opts = f.options || [];
      const disabled = f.dependsOn && !v[f.dependsOn];
      const ph = disabled ? `Choose ${f.dependsOn} first` : f.ph || "Select…";
      const onCh = (x) => {
        const patch = { [f.key]: x };
        (f.clears || []).forEach((k) => (patch[k] = ""));
        set(patch);
      };
      return (
        <Select
          value={v[f.key]}
          onChange={onCh}
          onBlur={blur}
          options={opts}
          placeholder={ph}
          disabled={disabled}
          error={err[f.key]}
        />
      );
    }
    const onInp = (x) => {
      if (f.transform === "upper") x = x.toUpperCase();
      if (f.maxLength) x = x.slice(0, f.maxLength);
      set({ [f.key]: x });
    };
    return (
      <TextInput
        value={v[f.key]}
        onChange={onInp}
        onBlur={blur}
        placeholder={f.ph}
        type={f.type}
        error={err[f.key]}
      />
    );
  };

  // group consecutive same-width fields into rows
  const rows = [];
  let cur = null;
  fields.forEach((f) => {
    const w = f.w || "full";
    if (w === "full") {
      rows.push({ w, items: [f] });
      cur = null;
    } else if (cur && cur.w === w) {
      cur.items.push(f);
    } else {
      cur = { w, items: [f] };
      rows.push(cur);
    }
  });

  return rows.map((row, i) => {
    if (row.w === "full") {
      const f = row.items[0];
      return (
        <div key={i} className="ev-row">
          <Field
            label={f.label}
            required={f.required}
            error={err[f.key]}
            hint={f.hint}
          >
            {control(f)}
          </Field>
        </div>
      );
    }
    return (
      <div
        key={i}
        className={`ev-row ${row.w === "third" ? "ev-grid3" : "ev-grid2"}`}
      >
        {row.items.map((f) => (
          <Field
            key={f.key}
            label={f.label}
            required={f.required}
            error={err[f.key]}
          >
            {control(f)}
          </Field>
        ))}
      </div>
    );
  });
};

export { Field, TextInput, Select, Segmented, Button, GuestFormFields };
